
/**
 * Module dependencies.
 */

import * as Sentry from '@sentry/browser';
import { SentryClientOptions, SentryOptions, SentryScopeOptions } from '../types';
import { isNil, isUndefined, omitBy } from 'lodash';
import yn from 'yn';

/**
 * `SentryShowReportDialogOptions` type.
 */

type SentryShowReportDialogOptions = {
  sentryEventId: string | undefined;
};

/**
 * `SentryCaptureExceptionOptions` type.
 */

type SentryCaptureExceptionOptions = {
  error: Error | null | undefined;
  errorInfo: any;
  severity?: string | Sentry.Severity;
  user?: any;
};

/**
 * `SentryClient`.
 */

class SentryClient {

  /**
   * Dsn Exists.
   */

  dsnExists = false;

  /**
   * Is report dialog enabled.
   */

  isReportDialogEnabled = false;

  /**
   * Init.
   *
   * Initializes the `SentryClient`.
   */

  init(sentryOptions: SentryOptions) {
    const { dsn, options, packageJson, reportDialog = false } = sentryOptions;

    if (isNil(dsn)) {
      return;
    }

    const { extras = {}, init = {}, tags = {} } = options;

    this.dsnExists = true;
    this.isReportDialogEnabled = yn(reportDialog) ?? false;
    this.initClient({ dsn, init });
    this.configureScope({ extras, packageJson, tags });
  }

  /**
   * Init client.
   *
   * Initializes the `Sentry` client with the given `dsn` and `init` options.
   */

  initClient({ dsn, init }: SentryClientOptions) {
    Sentry.init({
      dsn,
      ignoreErrors: [
        // Random plugins/extensions
        'top.GLOBALS',
        // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
        'originalCreateNotification',
        'canvas.contentDocument',
        'MyApp_RemoveAllHighlights',
        'http://tt.epicplay.com',
        'Can\'t find variable: ZiteReader',
        'jigsaw is not defined',
        'ComboSearch is not defined',
        'http://loading.retry.widdit.com/',
        'atomicFindClose',
        // Facebook borked
        'fb_xd_fragment',
        // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
        // reduce this. (thanks @acdha)
        // See http://stackoverflow.com/questions/4113268
        'bmi_SafeAddOnload',
        'EBCallBackMessageReceived',
        // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
        'conduitPage',
        // Errors we can ignore
        /ChunkLoadError/,
        /Network Error/,
        /SecurityError/,
        /vid_mate_check is not defined/
      ],
      ignoreUrls: [
        // Chrome extensions
        /chrome-extension:\/\//i,
        /extensions\//i,
        /^chrome:\/\//i,
        // Firefox extensions
        /^resource:\/\//i,
        // Ignore Google flakiness
        /\/(gtm|ga|analytics)\.js/i,
        // Other plugins
        /127\.0\.0\.1:4001\/isrunning/i,
        /webappstoolbarba\.texthelp\.com\//i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
      ],
      ...init
    });
  }

  /**
   * Configure scope.
   *
   * Configures the global scope for the client events according to the given
   * `extras` and `tags` options.
   */

  configureScope(scopeOptions: SentryScopeOptions) {
    const { extras, packageJson, tags } = scopeOptions;

    Sentry.setTags({
      name: `${packageJson?.name}:client`,
      version: packageJson?.version,
      ...tags
    });

    Sentry.setExtras({
      dependencies: packageJson?.dependencies,
      ...extras
    });
  }

  /**
   * Is report dialog to show.
   *
   * Informs if the report dialog feature is enabled or disabled.
   */

  isReportDialogToShow = () => this.dsnExists && this.isReportDialogEnabled;

  /**
   * Show report dialog.
   *
   * Shows the event report dialog through `Sentry`.
   */

  showReportDialog({ sentryEventId }: SentryShowReportDialogOptions) {
    Sentry.showReportDialog({ eventId: sentryEventId });
  }

  /**
   * Capture exception.
   *
   * Captures exception with specific event scope.
   */

  captureException(captureExceptionOptions: SentryCaptureExceptionOptions) {
    const { error, errorInfo, severity = 'error', user } = captureExceptionOptions;

    if (!this.dsnExists) {
      return;
    }

    const eventId = Sentry.withScope(scope => {
      scope.setLevel(severity as Sentry.Severity);
      scope.setUser(omitBy(user, isUndefined));
      scope.setExtras(errorInfo);

      const eventId = Sentry.captureException(error);

      return eventId;
    });

    return eventId;
  }

}

/**
 * Export `sentryClient`.
 */

export default new SentryClient();
